/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React from 'react';
import PropTypes from 'prop-types';
import { Tag, Plus, Trash2, Braces, KeyRound } from 'lucide-react';
import HierarchySelector from '../hierarchy/HierarchySelector';
import {
  KB_METADATA_MAX_PAIRS,
  KB_METADATA_KEY_MAX_LENGTH,
  KB_METADATA_VALUE_MAX_LENGTH,
} from '../../services/knowledgeBaseService';
import { SERVICE_REQUEST_LABEL } from '../../constants/entityLabels';

export const metadataInputClass = (hasError) =>
  `w-full px-4 py-2.5 bg-base-100 border rounded-xl text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all ${hasError ? 'border-error' : 'border-base-300'}`;

export const MetadataSectionHeader = ({ icon: Icon, title, badge = 'Optional' }) => (
  <div className="flex items-center justify-between pb-3 border-b border-base-content/10">
    <div className="flex items-center gap-2">
      <Icon size={18} className="text-primary" />
      <h2 className="text-base font-black text-base-content uppercase tracking-wider">{title}</h2>
    </div>
    {badge && <span className="text-[10px] text-base-content/40 font-mono">{badge}</span>}
  </div>
);

MetadataSectionHeader.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  badge: PropTypes.string,
};

/**
 * Service request type + hierarchy, custom key/value pairs and the metadata
 * preview of a knowledge base document. `form` is the useDocumentMetadataForm
 * state; used by the upload and edit pages.
 */
const DocumentMetadataFields = ({ form, disabled = false }) => (
  <>
    {/* Service Request Type — the type and its hierarchy levels */}
    <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
      <MetadataSectionHeader icon={Tag} title={`${SERVICE_REQUEST_LABEL.singular} Type`} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Type */}
        <div className="space-y-1.5">
          <label htmlFor="serviceRequestType" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
            <Tag size={12} className="text-primary" />
            <span>{SERVICE_REQUEST_LABEL.singular} Type</span>
          </label>
          <select
            id="serviceRequestType"
            className={metadataInputClass(false)}
            value={form.typeUid}
            disabled={form.typesLoading || disabled}
            onChange={(e) => form.changeType(e.target.value)}
          >
            <option value="">{form.typesLoading ? 'Loading…' : '— None —'}</option>
            {form.types.map((t) => (
              <option key={t.uid} value={t.uid}>{t.name}</option>
            ))}
          </select>
        </div>

        {/* Hierarchy — the type's levels, when configured */}
        {form.typeUid && (
          <HierarchySelector
            inline
            levels={form.levels}
            nodes={form.nodes}
            value={form.hierarchyValue}
            onChange={form.setHierarchyValue}
            loading={form.hierarchyLoading}
            selectClassName={metadataInputClass}
          />
        )}
      </div>
      <p className="text-base-content/40 text-[10px] font-medium">
        Enables the hierarchy selection when the type has one configured. The picked hierarchy
        levels are added to the metadata.
      </p>
    </div>

    {/* Custom Metadata */}
    <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
      <MetadataSectionHeader icon={KeyRound} title="Metadata" />
      <p className="text-xs text-base-content/60">
        Add custom key / value pairs to attach to this document. Rows left completely empty are ignored.
      </p>

      {form.pairs.length > 0 && (
        <div className="space-y-3">
          {form.pairs.map((p) => {
            const error = form.pairErrors[p.id];
            return (
              <div key={p.id} className="space-y-1">
                <div className="grid grid-cols-12 gap-2 items-center">
                  <input
                    type="text"
                    aria-label="Key"
                    placeholder="Key (e.g. floor)"
                    maxLength={KB_METADATA_KEY_MAX_LENGTH}
                    className={`col-span-5 ${metadataInputClass(Boolean(error))}`}
                    value={p.key}
                    disabled={disabled}
                    onChange={(e) => form.updatePair(p.id, 'key', e.target.value)}
                  />
                  <input
                    type="text"
                    aria-label="Value"
                    placeholder="Value (e.g. 3)"
                    maxLength={KB_METADATA_VALUE_MAX_LENGTH}
                    className={`col-span-6 ${metadataInputClass(Boolean(error))}`}
                    value={p.value}
                    disabled={disabled}
                    onChange={(e) => form.updatePair(p.id, 'value', e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => form.removePair(p.id)}
                    disabled={disabled}
                    className="col-span-1 justify-self-center p-1.5 text-base-content/40 hover:text-error hover:bg-error/10 rounded-lg transition-all"
                    aria-label="Remove key / value"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
                {error && <p className="text-error text-xs font-medium">{error}</p>}
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={form.addPair}
        disabled={disabled || form.pairs.length >= KB_METADATA_MAX_PAIRS}
        className="btn btn-sm btn-ghost border border-base-content/10 rounded-xl font-bold text-xs gap-1.5"
      >
        <Plus size={14} />
        <span>Add key / value</span>
      </button>
    </div>

    {/* Metadata Preview */}
    <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
      <MetadataSectionHeader icon={Braces} title="Metadata Preview" badge={null} />
      <pre className="p-4 rounded-xl bg-base-200/50 border border-base-content/5 text-xs font-mono text-base-content/80 overflow-x-auto whitespace-pre-wrap break-all">
        {JSON.stringify(form.previewMetadata, null, 2)}
      </pre>
    </div>
  </>
);

DocumentMetadataFields.propTypes = {
  // useDocumentMetadataForm() result
  form: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
};

export default DocumentMetadataFields;
